<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Consultation</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('queue-management')">Queue</a></li>
                      <li class="breadcrumb-item active">Consultation</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading visit details...</p>
            </div>

            <div v-else class="row">
              <!-- Left Column: Patient & Vitals Info -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <h6 class="mb-0"><i class="mdi mdi-account me-2 text-primary"></i>Patient</h6>
                      <span class="queue-badge-sm">{{ visit.queueNumber }}</span>
                    </div>

                    <div class="text-center mb-3">
                      <div class="avatar-lg mx-auto mb-2">
                          <span class="avatar-title rounded-circle" :class="visit.patientGender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.5rem;">
                            {{ getInitials(visit.patientName) }}
                          </span>
                      </div>
                      <h5 class="mb-1">{{ visit.patientName }}</h5>
                      <div class="d-flex justify-content-center flex-wrap gap-1">
                        <span class="badge bg-light text-dark">{{ visit.patientID }}</span>
                        <span class="badge" :class="visit.patientGender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-pink-subtle text-pink'">{{ visit.patientGender }}</span>
                        <span class="badge bg-light text-dark">{{ visit.patientAge }} yrs</span>
                        <span v-if="visit.bloodGroup" class="badge bg-danger-subtle text-danger">{{ visit.bloodGroup }}</span>
                      </div>
                    </div>

                    <!-- Medical Alerts -->
                    <div v-if="visit.allergies" class="alert alert-danger py-2 mb-2">
                      <small><i class="mdi mdi-alert-circle me-1"></i><strong>Allergies:</strong> {{ visit.allergies }}</small>
                    </div>
                    <div v-if="visit.chronicConditions" class="alert alert-warning py-2 mb-0">
                      <small><i class="mdi mdi-heart-pulse me-1"></i><strong>Chronic:</strong> {{ visit.chronicConditions }}</small>
                    </div>
                  </div>
                </div>

                <!-- Vitals Card -->
                <div class="card" v-if="triage">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0"><i class="mdi mdi-pulse me-2 text-primary"></i>Vitals</h6>
                      <span class="badge" :class="getTriageBadge(triage.triageCategory)">{{ triage.triageCategory }}</span>
                    </div>

                    <div class="row g-2">
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">BP</span>
                          <span class="vital-value">{{ triage.bloodPressure || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">Temp</span>
                          <span class="vital-value">{{ triage.temperature }}°C</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">Pulse</span>
                          <span class="vital-value">{{ triage.pulseRate }} bpm</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">SpO2</span>
                          <span class="vital-value">{{ triage.oxygenSaturation }}%</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">Weight</span>
                          <span class="vital-value">{{ triage.weight }} kg</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="vital-mini">
                          <span class="vital-label">Height</span>
                          <span class="vital-value">{{ triage.height }} cm</span>
                        </div>
                      </div>
                    </div>

                    <div v-if="triage.chiefComplaint" class="mt-3 pt-3 border-top">
                      <small class="text-muted d-block mb-1">Chief Complaint</small>
                      <p class="mb-0">{{ triage.chiefComplaint }}</p>
                    </div>
                  </div>
                </div>

                <!-- Visit Info -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-clipboard-text me-2 text-primary"></i>Visit Info</h6>
                    <table class="table table-sm table-borderless mb-0">
                      <tbody>
                      <tr>
                        <td class="text-muted">Visit ID</td>
                        <td class="text-end fw-medium">{{ visit.visitID }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Type</td>
                        <td class="text-end"><span class="badge bg-primary">{{ visit.visitType }}</span></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Payment</td>
                        <td class="text-end">{{ visit.paymentMode }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Check-in</td>
                        <td class="text-end">{{ formatDateTime(visit.dateCreated) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Quick Actions -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-lightning-bolt me-2 text-primary"></i>Quick Actions</h6>
                    <div class="d-grid gap-2">
                      <button class="btn btn-outline-primary btn-sm" @click="showLabRequestModal">
                        <i class="mdi mdi-test-tube me-1"></i> Order Lab Tests
                      </button>
                      <button class="btn btn-outline-warning btn-sm" @click="showRadiologyModal">
                        <i class="mdi mdi-radiology-box me-1"></i> Request Radiology
                      </button>
                      <button class="btn btn-outline-danger btn-sm" @click="showSurgeryModal">
                        <i class="mdi mdi-hospital-box me-1"></i> Request Surgery
                      </button>
                      <button class="btn btn-outline-info btn-sm" @click="viewPatientHistory">
                        <i class="mdi mdi-history me-1"></i> Patient History
                      </button>
                      <button class="btn btn-outline-secondary btn-sm" @click="printSummary">
                        <i class="mdi mdi-printer me-1"></i> Print Summary
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Consultation Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-stethoscope me-2 text-primary"></i>Consultation Notes</h5>
                        <small class="text-muted">Dr. {{ sessionNames }}</small>
                      </div>
                      <div class="d-flex gap-2">
                          <span v-if="consultationStartTime" class="badge bg-success-subtle text-success">
                            <i class="mdi mdi-clock-outline me-1"></i>{{ getElapsedTime() }}
                          </span>
                      </div>
                    </div>

                    <form @submit.prevent="saveConsultation">
                      <!-- Tabs for sections -->
                      <ul class="nav nav-tabs nav-tabs-custom mb-4">
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'" href="javascript:void(0);">
                            <i class="mdi mdi-clipboard-text-outline me-1"></i> History
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'examination' }" @click="activeTab = 'examination'" href="javascript:void(0);">
                            <i class="mdi mdi-stethoscope me-1"></i> Examination
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'diagnosis' }" @click="activeTab = 'diagnosis'" href="javascript:void(0);">
                            <i class="mdi mdi-medical-bag me-1"></i> Diagnosis
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'treatment' }" @click="activeTab = 'treatment'" href="javascript:void(0);">
                            <i class="mdi mdi-pill me-1"></i> Treatment
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'followup' }" @click="activeTab = 'followup'" href="javascript:void(0);">
                            <i class="mdi mdi-calendar-check me-1"></i> Follow-up
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'labresults' }" @click="activeTab = 'labresults'; loadLabResults();" href="javascript:void(0);">
                            <i class="mdi mdi-flask me-1"></i> Lab
                            <span v-if="labResults.length > 0" class="badge bg-info ms-1">{{ labResults.length }}</span>
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'radresults' }" @click="activeTab = 'radresults'; loadRadiologyResults();" href="javascript:void(0);">
                            <i class="mdi mdi-radiology-box me-1"></i> Radiology
                            <span v-if="radiologyResults.length > 0" class="badge bg-warning text-dark ms-1">{{ radiologyResults.length }}</span>
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'surgresults' }" @click="activeTab = 'surgresults'; loadSurgeryResults();" href="javascript:void(0);">
                            <i class="mdi mdi-hospital-box me-1"></i> Surgery
                            <span v-if="surgeryResults.length > 0" class="badge bg-danger ms-1">{{ surgeryResults.length }}</span>
                          </a>
                        </li>
                      </ul>

                      <!-- History Tab -->
                      <div v-show="activeTab === 'history'">
                        <div class="row">
                          <div class="col-12 mb-3">
                            <label class="form-label">Chief Complaint <span class="text-danger">*</span></label>
                            <textarea class="form-control" rows="2" v-model="consultation.chiefComplaint"
                                      placeholder="Main reason for visit..." required></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">History of Present Illness</label>
                            <textarea class="form-control" rows="3" v-model="consultation.historyOfPresentIllness"
                                      placeholder="Onset, duration, progression, associated symptoms..."></textarea>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Past Medical History</label>
                            <textarea class="form-control" rows="2" v-model="consultation.pastMedicalHistory"
                                      placeholder="Previous illnesses, surgeries, hospitalizations..."></textarea>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Family History</label>
                            <textarea class="form-control" rows="2" v-model="consultation.familyHistory"
                                      placeholder="Family medical conditions..."></textarea>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Social History</label>
                            <textarea class="form-control" rows="2" v-model="consultation.socialHistory"
                                      placeholder="Occupation, smoking, alcohol, lifestyle..."></textarea>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label class="form-label">Review of Systems</label>
                            <textarea class="form-control" rows="2" v-model="consultation.reviewOfSystems"
                                      placeholder="Systematic review..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Examination Tab -->
                      <div v-show="activeTab === 'examination'">
                        <div class="row">
                          <div class="col-12 mb-3">
                            <label class="form-label">Physical Examination <span class="text-danger">*</span></label>
                            <textarea class="form-control" rows="4" v-model="consultation.physicalExamination"
                                      placeholder="General appearance, vital signs review, systematic examination..." required></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Systemic Examination</label>
                            <textarea class="form-control" rows="4" v-model="consultation.systemicExamination"
                                      placeholder="CVS, RS, GIT, CNS findings..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Diagnosis Tab -->
                      <div v-show="activeTab === 'diagnosis'">
                        <div class="row">
                          <div class="col-12 mb-3">
                            <label class="form-label">Provisional Diagnosis <span class="text-danger">*</span></label>
                            <textarea class="form-control" rows="2" v-model="consultation.provisionalDiagnosis"
                                      placeholder="Working diagnosis..." required></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">ICD-10 Codes</label>
                            <div class="input-group">
                              <input type="text" class="form-control" v-model="icdSearch"
                                     placeholder="Search ICD-10 code or description..." @input="searchICD">
                              <button class="btn btn-outline-secondary" type="button" @click="searchICD">
                                <i class="mdi mdi-magnify"></i>
                              </button>
                            </div>
                            <!-- ICD Search Results -->
                            <div v-if="icdResults.length > 0" class="icd-results mt-2">
                              <div v-for="icd in icdResults" :key="icd.code" class="icd-item" @click="addICD(icd)">
                                <span class="icd-code">{{ icd.code }}</span>
                                <span class="icd-desc">{{ icd.description }}</span>
                              </div>
                            </div>
                            <!-- Selected ICD Codes -->
                            <div v-if="selectedICDs.length > 0" class="mt-2">
                                <span v-for="(icd, index) in selectedICDs" :key="index" class="badge bg-primary me-1 mb-1">
                                  {{ icd.code }} - {{ icd.description }}
                                  <i class="mdi mdi-close ms-1" style="cursor:pointer;" @click="removeICD(index)"></i>
                                </span>
                            </div>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Differential Diagnosis</label>
                            <textarea class="form-control" rows="2" v-model="consultation.differentialDiagnosis"
                                      placeholder="Other possible diagnoses..."></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Final Diagnosis</label>
                            <textarea class="form-control" rows="2" v-model="consultation.finalDiagnosis"
                                      placeholder="Confirmed diagnosis (if applicable)..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Treatment Tab -->
                      <div v-show="activeTab === 'treatment'">
                        <div class="row">
                          <div class="col-12 mb-3">
                            <label class="form-label">Treatment Plan <span class="text-danger">*</span></label>
                            <textarea class="form-control" rows="4" v-model="consultation.treatmentPlan"
                                      placeholder="Medications, procedures, lifestyle modifications..." required></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Investigations Ordered</label>
                            <textarea class="form-control" rows="2" v-model="consultation.investigationsOrdered"
                                      placeholder="Lab tests, imaging, other investigations..."></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Procedures Performed</label>
                            <textarea class="form-control" rows="2" v-model="consultation.proceduresPerformed"
                                      placeholder="Any procedures done during consultation..."></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Patient Education</label>
                            <textarea class="form-control" rows="2" v-model="consultation.patientEducation"
                                      placeholder="Instructions given to patient..."></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">Doctor's Notes</label>
                            <textarea class="form-control" rows="2" v-model="consultation.doctorNotes"
                                      placeholder="Additional clinical notes..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Follow-up Tab -->
                      <div v-show="activeTab === 'followup'">
                        <div class="row">
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Follow-up Required</label>
                            <select class="form-select" v-model="consultation.followUpRequired">
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          <div class="col-md-4 mb-3" v-if="consultation.followUpRequired === 'Yes'">
                            <label class="form-label">Follow-up Date</label>
                            <input type="date" class="form-control" v-model="consultation.followUpDate">
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Sick-off Days</label>
                            <input type="number" class="form-control" v-model="consultation.sickOffDays" min="0" placeholder="0">
                          </div>
                          <div class="col-12 mb-3" v-if="consultation.followUpRequired === 'Yes'">
                            <label class="form-label">Follow-up Notes</label>
                            <textarea class="form-control" rows="2" v-model="consultation.followUpNotes"
                                      placeholder="Follow-up instructions..."></textarea>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Referral Required</label>
                            <select class="form-select" v-model="consultation.referralRequired">
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          <div class="col-md-8 mb-3" v-if="consultation.referralRequired === 'Yes'">
                            <label class="form-label">Refer To</label>
                            <input type="text" class="form-control" v-model="consultation.referralTo" placeholder="Specialist/Department">
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Admission Required</label>
                            <select class="form-select" v-model="consultation.admissionRequired">
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          <div class="col-md-8 mb-3" v-if="consultation.admissionRequired === 'Yes'">
                            <label class="form-label">Admission Ward</label>
                            <input type="text" class="form-control" v-model="consultation.admissionWard" placeholder="Ward name">
                          </div>
                        </div>
                      </div>

                      <!-- Lab Results Tab -->
                      <div v-show="activeTab === 'labresults'">
                        <div v-if="loadingLabResults" class="text-center py-4">
                          <div class="spinner-border spinner-border-sm text-primary"></div>
                          <span class="ms-2 text-muted">Loading lab results...</span>
                        </div>

                        <div v-else-if="labResults.length === 0" class="text-center py-4">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-flask-empty-outline"></i>
                          </div>
                          <h6 class="text-muted">No lab results for this visit</h6>
                          <button class="btn btn-soft-primary btn-sm mt-2" @click="showLabRequestModal">
                            <i class="mdi mdi-plus me-1"></i> Order Lab Test
                          </button>
                        </div>

                        <div v-else>
                          <!-- Order New Test Button -->
                          <div class="d-flex justify-content-end mb-3">
                            <button class="btn btn-soft-primary btn-sm" @click="showLabRequestModal">
                              <i class="mdi mdi-plus me-1"></i> Order New Test
                            </button>
                          </div>

                          <!-- Results Table -->
                          <div class="table-responsive">
                            <table class="table table-hover align-middle">
                              <thead class="table-light">
                              <tr>
                                <th>Test</th>
                                <th>Result</th>
                                <th>Reference</th>
                                <th>Flag</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th style="width: 100px;">Actions</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="lab in labResults" :key="lab.requestID">
                                <td>
                                  <span class="fw-medium">{{ lab.testName }}</span>
                                  <small class="text-muted d-block">{{ lab.sampleType }}</small>
                                </td>
                                <td>
                                      <span v-if="lab.result" class="fw-bold" :class="getLabFlagClass(lab.flag)">
                                        {{ lab.result }} {{ lab.unit }}
                                      </span>
                                  <span v-else class="text-muted">Pending</span>
                                </td>
                                <td>
                                  <small>{{ lab.normalRange || '-' }}</small>
                                </td>
                                <td>
                                  <span v-if="lab.flag" class="badge" :class="getLabFlagBadge(lab.flag)">{{ lab.flag }}</span>
                                  <span v-else-if="lab.result" class="text-success"><i class="mdi mdi-check-circle"></i></span>
                                  <span v-else>-</span>
                                </td>
                                <td>
                                  <span class="badge" :class="getLabStatusBadge(lab.requestStatus)">{{ lab.requestStatus }}</span>
                                  <small v-if="lab.resultStatus === 'Verified'" class="text-success d-block">
                                    <i class="mdi mdi-check-decagram"></i> Verified
                                  </small>
                                </td>
                                <td>
                                  <small>{{ formatLabDate(lab.resultDate || lab.requestDate) }}</small>
                                  <small class="text-muted d-block">{{ lab.labTechName }}</small>
                                </td>
                                <td>
                                  <button v-if="lab.requestStatus === 'Completed'"
                                          class="btn btn-sm btn-soft-success"
                                          @click="viewLabReport(lab.requestID)"
                                          title="View Full Report">
                                    <i class="mdi mdi-file-document-outline me-1"></i> Report
                                  </button>
                                  <span v-else class="text-muted small">-</span>
                                </td>
                              </tr>
                              </tbody>
                            </table>
                          </div>

                          <!-- Interpretation Section -->
                          <div v-for="lab in labResults.filter(l => l.interpretation)" :key="'interp-' + lab.requestID"
                               class="alert alert-light mt-3">
                            <h6 class="mb-1">{{ lab.testName }} - Interpretation</h6>
                            <p class="mb-0 small">{{ lab.interpretation }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Radiology Results Tab -->
                      <div v-show="activeTab === 'radresults'">
                        <div v-if="loadingRadResults" class="text-center py-4">
                          <div class="spinner-border spinner-border-sm text-warning"></div>
                          <span class="ms-2 text-muted">Loading radiology results...</span>
                        </div>

                        <div v-else-if="radiologyResults.length === 0" class="text-center py-4">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-radiology-box-outline"></i>
                          </div>
                          <h6 class="text-muted">No radiology results for this visit</h6>
                          <button class="btn btn-soft-warning btn-sm mt-2" @click="showRadiologyModal">
                            <i class="mdi mdi-plus me-1"></i> Request Radiology
                          </button>
                        </div>

                        <div v-else>
                          <!-- Request New Radiology Button -->
                          <div class="d-flex justify-content-end mb-3">
                            <button class="btn btn-soft-warning btn-sm" @click="showRadiologyModal">
                              <i class="mdi mdi-plus me-1"></i> Request New Radiology
                            </button>
                          </div>

                          <!-- Radiology Cards -->
                          <div v-for="rad in radiologyResults" :key="rad.requestID" class="card mb-3 border">
                            <div class="card-header d-flex justify-content-between align-items-center"
                                 :class="rad.status === 'Completed' ? 'bg-success text-white' : 'bg-warning text-dark'">
                              <div>
                                <h6 class="mb-0">{{ rad.examName }}</h6>
                                <small>{{ rad.modalityType }} • {{ rad.bodyPart }} {{ rad.laterality ? '(' + rad.laterality + ')' : '' }}</small>
                              </div>
                              <span class="badge" :class="rad.status === 'Completed' ? 'bg-light text-success' : 'bg-light text-warning'">
                                {{ rad.status }}
                              </span>
                            </div>
                            <div class="card-body">
                              <!-- Pending Status -->
                              <div v-if="rad.status !== 'Completed'" class="text-center py-3">
                                <i class="mdi mdi-clock-outline font-size-32 text-warning"></i>
                                <p class="text-muted mb-0">Awaiting radiology results</p>
                                <small class="text-muted">Requested: {{ formatDateTime(rad.dateCreated) }}</small>
                              </div>

                              <!-- Completed - Show Results -->
                              <div v-else>
                                <div class="row mb-3">
                                  <div class="col-md-6">
                                    <small class="text-muted">Clinical Indication</small>
                                    <p class="mb-0">{{ rad.clinicalIndication || '-' }}</p>
                                  </div>
                                  <div class="col-md-6 text-md-end">
                                    <small class="text-muted">Report Date</small>
                                    <p class="mb-0">{{ formatDateTime(rad.reportDate) }}</p>
                                  </div>
                                </div>

                                <div v-if="rad.technique" class="mb-3">
                                  <small class="text-muted">Technique</small>
                                  <p class="mb-0">{{ rad.technique }}</p>
                                </div>

                                <div class="mb-3">
                                  <small class="text-muted">Findings</small>
                                  <div class="border rounded p-2 bg-light" style="white-space: pre-wrap;">{{ rad.findings }}</div>
                                </div>

                                <div class="mb-3">
                                  <small class="text-muted">Impression</small>
                                  <div class="alert alert-success py-2 mb-0 fw-bold" style="white-space: pre-wrap;">{{ rad.impression }}</div>
                                </div>

                                <div v-if="rad.recommendations" class="mb-3">
                                  <small class="text-muted">Recommendations</small>
                                  <p class="mb-0">{{ rad.recommendations }}</p>
                                </div>

                                <div class="border-top pt-2 mt-2 d-flex justify-content-between align-items-center">
                                  <small class="text-muted">
                                    <i class="mdi mdi-doctor me-1"></i>Reported by: {{ rad.radiologistName }}
                                  </small>
                                  <button class="btn btn-sm btn-outline-primary" @click="printRadiologyReport(rad)">
                                    <i class="mdi mdi-printer me-1"></i> Print
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Surgery Results Tab -->
                      <div v-show="activeTab === 'surgresults'">
                        <div v-if="loadingSurgResults" class="text-center py-4">
                          <div class="spinner-border spinner-border-sm text-danger"></div>
                          <span class="ms-2 text-muted">Loading surgery records...</span>
                        </div>

                        <div v-else-if="surgeryResults.length === 0" class="text-center py-4">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-hospital-box-outline"></i>
                          </div>
                          <h6 class="text-muted">No surgery requests for this visit</h6>
                          <button class="btn btn-soft-danger btn-sm mt-2" @click="showSurgeryModal">
                            <i class="mdi mdi-plus me-1"></i> Request Surgery
                          </button>
                        </div>

                        <div v-else>
                          <!-- Request New Surgery Button -->
                          <div class="d-flex justify-content-end mb-3">
                            <button class="btn btn-soft-danger btn-sm" @click="showSurgeryModal">
                              <i class="mdi mdi-plus me-1"></i> Request New Surgery
                            </button>
                          </div>

                          <!-- Surgery Cards -->
                          <div v-for="surg in surgeryResults" :key="surg.requestID" class="card mb-3 border">
                            <div class="card-header d-flex justify-content-between align-items-center"
                                 :class="getSurgeryHeaderClass(surg.status)">
                              <div>
                                <h6 class="mb-0">{{ surg.procedureName }}</h6>
                                <small>
                                  {{ surg.surgeryType }} • {{ surg.surgeryCategory || 'Surgery' }}
                                  <span v-if="surg.bodyPart"> • {{ surg.bodyPart }}</span>
                                  <span v-if="surg.laterality"> ({{ surg.laterality }})</span>
                                </small>
                              </div>
                              <span class="badge" :class="getSurgeryStatusBadge(surg.status)">
                                {{ surg.status }}
                              </span>
                            </div>
                            <div class="card-body">
                              <!-- Request Info (for all statuses) -->
                              <div class="row mb-3">
                                <div class="col-md-6">
                                  <small class="text-muted">Pre-Op Diagnosis</small>
                                  <p class="mb-0 fw-medium">{{ surg.diagnosis || '-' }}</p>
                                </div>
                                <div class="col-md-3">
                                  <small class="text-muted">Scheduled</small>
                                  <p class="mb-0">{{ surg.scheduledDate ? formatDate(surg.scheduledDate) : 'Not scheduled' }}</p>
                                </div>
                                <div class="col-md-3">
                                  <small class="text-muted">Priority</small>
                                  <p class="mb-0">
                                    <span class="badge" :class="getSurgeryPriorityBadge(surg.priority)">{{ surg.priority }}</span>
                                  </p>
                                </div>
                              </div>

                              <!-- Pending/Scheduled Status -->
                              <div v-if="['Requested', 'Scheduled', 'Pre-Op'].includes(surg.status)" class="text-center py-3 bg-light rounded">
                                <i class="mdi mdi-clock-outline font-size-32 text-warning"></i>
                                <p class="text-muted mb-1">Surgery {{ surg.status === 'Requested' ? 'pending scheduling' : (surg.status === 'Scheduled' ? 'scheduled' : 'in pre-op preparation') }}</p>
                                <small class="text-muted">Requested: {{ formatDateTime(surg.dateCreated) }}</small>
                                <div v-if="surg.anesthesiaType" class="mt-2">
                                  <span class="badge bg-info">{{ surg.anesthesiaType }} Anesthesia</span>
                                  <span v-if="surg.anesthesiaRisk" class="badge bg-secondary ms-1">{{ surg.anesthesiaRisk }}</span>
                                </div>
                              </div>

                              <!-- In Theatre Status -->
                              <div v-else-if="surg.status === 'In Theatre'" class="text-center py-3 bg-danger-subtle rounded">
                                <i class="mdi mdi-hospital-building font-size-32 text-danger mdi-spin"></i>
                                <p class="text-danger fw-bold mb-1">Surgery In Progress</p>
                                <small class="text-muted">Theatre: {{ surg.theatreRoom || '-' }}</small>
                                <div v-if="surg.surgeonName" class="mt-2">
                                  <small class="text-muted">Surgeon: {{ surg.surgeonName }}</small>
                                </div>
                              </div>

                              <!-- Completed - Show Operative Notes -->
                              <div v-else-if="surg.status === 'Completed'">
                                <!-- Surgery Team -->
                                <div class="row mb-3">
                                  <div class="col-md-4">
                                    <small class="text-muted">Surgeon</small>
                                    <p class="mb-0 fw-medium">{{ surg.surgeonName || '-' }}</p>
                                  </div>
                                  <div class="col-md-4">
                                    <small class="text-muted">Anesthetist</small>
                                    <p class="mb-0">{{ surg.anesthetistName || '-' }}</p>
                                  </div>
                                  <div class="col-md-4">
                                    <small class="text-muted">Theatre</small>
                                    <p class="mb-0">{{ surg.theatreRoom || '-' }}</p>
                                  </div>
                                </div>

                                <!-- Surgery Times -->
                                <div class="row mb-3">
                                  <div class="col-md-3">
                                    <small class="text-muted">Surgery Start</small>
                                    <p class="mb-0">{{ surg.surgeryStartTime ? formatTime(surg.surgeryStartTime) : '-' }}</p>
                                  </div>
                                  <div class="col-md-3">
                                    <small class="text-muted">Surgery End</small>
                                    <p class="mb-0">{{ surg.surgeryEndTime ? formatTime(surg.surgeryEndTime) : '-' }}</p>
                                  </div>
                                  <div class="col-md-3">
                                    <small class="text-muted">Duration</small>
                                    <p class="mb-0">{{ calculateDuration(surg.surgeryStartTime, surg.surgeryEndTime) }}</p>
                                  </div>
                                  <div class="col-md-3">
                                    <small class="text-muted">Blood Loss</small>
                                    <p class="mb-0">{{ surg.bloodLoss ? surg.bloodLoss + ' ml' : '-' }}</p>
                                  </div>
                                </div>

                                <!-- Post-Op Diagnosis -->
                                <div class="alert alert-success py-2 mb-3">
                                  <small class="text-muted d-block">Post-Op Diagnosis</small>
                                  <span class="fw-bold">{{ surg.postOpDiagnosis || surg.diagnosis || '-' }}</span>
                                </div>

                                <!-- Operative Findings -->
                                <div class="mb-3">
                                  <small class="text-muted">Operative Findings</small>
                                  <div class="border rounded p-2 bg-light" style="white-space: pre-wrap;">{{ surg.operativeFindings || '-' }}</div>
                                </div>

                                <!-- Procedure Details -->
                                <div class="mb-3">
                                  <small class="text-muted">Procedure Details</small>
                                  <div class="border rounded p-2 bg-light" style="white-space: pre-wrap;">{{ surg.procedureDetails || '-' }}</div>
                                </div>

                                <!-- Complications -->
                                <div v-if="surg.complications" class="alert alert-warning py-2 mb-3">
                                  <small class="text-muted d-block">Complications</small>
                                  <span>{{ surg.complications }}</span>
                                </div>

                                <!-- Additional Info -->
                                <div class="row mb-3">
                                  <div class="col-md-4" v-if="surg.specimens">
                                    <small class="text-muted">Specimens</small>
                                    <p class="mb-0">{{ surg.specimens }}</p>
                                  </div>
                                  <div class="col-md-4" v-if="surg.drains">
                                    <small class="text-muted">Drains</small>
                                    <p class="mb-0">{{ surg.drains }}</p>
                                  </div>
                                  <div class="col-md-4" v-if="surg.implants">
                                    <small class="text-muted">Implants</small>
                                    <p class="mb-0">{{ surg.implants }}</p>
                                  </div>
                                </div>

                                <!-- Post-Op Instructions -->
                                <div v-if="surg.postOpInstructions" class="mb-3">
                                  <small class="text-muted">Post-Op Instructions</small>
                                  <div class="border rounded p-2 bg-info-subtle" style="white-space: pre-wrap;">{{ surg.postOpInstructions }}</div>
                                </div>

                                <!-- Post-Op Destination -->
                                <div class="row mb-3">
                                  <div class="col-md-6">
                                    <small class="text-muted">Post-Op Destination</small>
                                    <p class="mb-0">
                                      <span class="badge bg-primary">{{ surg.postOpDestination || 'Ward' }}</span>
                                    </p>
                                  </div>
                                  <div class="col-md-6">
                                    <small class="text-muted">Safety Counts</small>
                                    <p class="mb-0">
                                      <span v-if="surg.swabCountCorrect === 'Yes'" class="badge bg-success me-1"><i class="mdi mdi-check"></i> Swabs</span>
                                      <span v-if="surg.instrumentCountCorrect === 'Yes'" class="badge bg-success me-1"><i class="mdi mdi-check"></i> Instruments</span>
                                      <span v-if="surg.needleCountCorrect === 'Yes'" class="badge bg-success"><i class="mdi mdi-check"></i> Needles</span>
                                    </p>
                                  </div>
                                </div>

                                <!-- Print Button -->
                                <div class="border-top pt-3 mt-3 d-flex justify-content-between align-items-center">
                                  <small class="text-muted">
                                    <i class="mdi mdi-calendar me-1"></i>Completed: {{ formatDateTime(surg.completedDate || surg.dateModified) }}
                                  </small>
                                  <button class="btn btn-sm btn-outline-danger" @click="printOperativeNotes(surg)">
                                    <i class="mdi mdi-printer me-1"></i> Print Operative Notes
                                  </button>
                                </div>
                              </div>

                              <!-- Cancelled/Postponed -->
                              <div v-else-if="['Cancelled', 'Postponed'].includes(surg.status)" class="text-center py-3 bg-light rounded">
                                <i class="mdi mdi-cancel font-size-32 text-secondary"></i>
                                <p class="text-muted mb-1">Surgery {{ surg.status }}</p>
                                <small v-if="surg.cancellationReason" class="text-muted">Reason: {{ surg.cancellationReason }}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-flex justify-content-between align-items-center pt-4 border-top mt-4">
                        <button type="button" class="btn btn-light" @click="goTo('queue-management')">
                          <i class="mdi mdi-arrow-left me-1"></i> Back to Queue
                        </button>
                        <div class="d-flex gap-2">
                          <button type="button" class="btn btn-outline-primary" @click="saveDraft" :disabled="saving">
                            <i class="mdi mdi-content-save-outline me-1"></i> Save Draft
                          </button>
                          <button type="button" class="btn btn-info" @click="showPrescriptionModal">
                            <i class="mdi mdi-pill me-1"></i> Write Prescription
                          </button>
                          <button type="submit" class="btn btn-success" :disabled="!canSubmit || saving">
                            <span v-if="saving"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
                            <span v-else><i class="mdi mdi-check me-1"></i> Complete Consultation</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Lab Request Modal -->
    <div class="modal fade" id="labRequestModal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title"><i class="mdi mdi-test-tube me-2"></i>Order Lab Tests</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Select Category (Income Type) -->
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Lab Category <span class="text-danger">*</span></label>
                <select class="form-select" v-model="labSelectedIncomeType" @change="onLabIncomeTypeChange">
                  <option value="">-- Select Category --</option>
                  <option v-for="item in labIncomeTypes" :key="item.incomeTypeId" :value="item.incomeTypeId">
                    {{ item.incomeTypeDescription }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Test / Service <span class="text-danger">*</span></label>
                <select class="form-select" v-model="labSelectedFee" @change="onLabFeeChange" :disabled="!labFeesAndCharges.length || loadingLabFees">
                  <option value="">-- Select Test --</option>
                  <option v-for="item in labFeesAndCharges" :key="item.feeId" :value="item.feeId">
                    {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                  </option>
                </select>
                <small v-if="loadingLabFees" class="text-muted">
                  <i class="mdi mdi-loading mdi-spin me-1"></i>Loading tests...
                </small>
              </div>
            </div>

            <!-- Selected Test Info -->
            <div v-if="labSelectedFeeData" class="alert alert-info mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ labSelectedFeeData.feeDescription }}</strong>
                  <small class="text-muted d-block">{{ labSelectedFeeData.accountDesc }}</small>
                </div>
                <div class="text-end">
                  <h5 class="mb-0 text-primary">KES {{ numberFormat(labSelectedFeeData.unitFeeAmount) }}</h5>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-primary mt-2" @click="addLabTestFromBiller" :disabled="!labSelectedFeeData">
                <i class="mdi mdi-plus me-1"></i> Add to Order
              </button>
            </div>

            <!-- Selected Tests List -->
            <div v-if="selectedLabTests.length > 0" class="mb-3">
              <label class="form-label">Selected Tests ({{ selectedLabTests.length }})</label>
              <div class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="table-light">
                  <tr>
                    <th>Test Name</th>
                    <th class="text-end" style="width: 120px;">Price</th>
                    <th class="text-center" style="width: 60px;">Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(test, index) in selectedLabTests" :key="index">
                    <td>
                      {{ test.testName }}
                      <small v-if="test.incomeTypeDesc" class="text-muted d-block">{{ test.incomeTypeDesc }}</small>
                    </td>
                    <td class="text-end">KES {{ numberFormat(test.unitPrice || 0) }}</td>
                    <td class="text-center">
                      <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removeLabTest(index)">
                        <i class="mdi mdi-close-circle"></i>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <th>Total</th>
                    <th class="text-end text-success">KES {{ numberFormat(labOrderTotal) }}</th>
                    <th></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- No tests selected message -->
            <div v-else class="alert alert-light text-center mb-3">
              <i class="mdi mdi-flask-empty-outline font-size-24 d-block mb-2 text-muted"></i>
              <span class="text-muted">Select a category and test to add to order</span>
            </div>

            <hr>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Priority</label>
                <select class="form-select" v-model="labPriority">
                  <option value="ROUTINE">Routine</option>
                  <option value="URGENT">Urgent</option>
                  <option value="STAT">STAT (Emergency)</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Clinical Notes</label>
                <textarea class="form-control" rows="2" v-model="labClinicalNotes" placeholder="Reason for tests..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="me-auto" v-if="selectedLabTests.length > 0">
              <strong class="text-success">Total: KES {{ numberFormat(labOrderTotal) }}</strong>
            </div>
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="submitLabRequest" :disabled="selectedLabTests.length === 0 || submittingLab">
              <span v-if="submittingLab"><i class="mdi mdi-loading mdi-spin me-1"></i> Submitting...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Submit Order ({{ selectedLabTests.length }})</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Prescription Modal -->
    <div class="modal fade" id="prescriptionModal" tabindex="-1">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title"><i class="mdi mdi-pill me-2"></i>Write Prescription</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Select Category and Drug -->
            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <select class="form-select" v-model="rxSelectedIncomeType" @change="onRxIncomeTypeChange">
                  <option value="">-- Select Category --</option>
                  <option v-for="item in rxIncomeTypes" :key="item.incomeTypeId" :value="item.incomeTypeId">
                    {{ item.incomeTypeDescription }}
                  </option>
                </select>
              </div>
              <div class="col-md-5">
                <label class="form-label">Medication <span class="text-danger">*</span></label>
                <select class="form-select" v-model="rxSelectedFee" @change="onRxFeeChange" :disabled="!rxFeesAndCharges.length || loadingRxFees">
                  <option value="">-- Select Medication --</option>
                  <option v-for="item in rxFeesAndCharges" :key="item.feeId" :value="item.feeId">
                    {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                  </option>
                </select>
                <small v-if="loadingRxFees" class="text-muted">
                  <i class="mdi mdi-loading mdi-spin me-1"></i>Loading medications...
                </small>
              </div>
              <div class="col-md-3">
                <label class="form-label">Unit Price</label>
                <input type="text" class="form-control bg-light"
                       :value="rxSelectedFeeData ? 'KES ' + numberFormat(rxSelectedFeeData.unitFeeAmount) : ''" readonly>
              </div>
            </div>

            <!-- Drug Details Form -->
            <div v-if="rxSelectedFeeData" class="card card-body bg-light mb-3">
              <div class="row">
                <div class="col-md-3 mb-2">
                  <label class="form-label">Dosage <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="rxDosage" placeholder="e.g., 500mg">
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Frequency <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="rxFrequency">
                    <option value="">-- Select --</option>
                    <option value="OD">Once Daily (OD)</option>
                    <option value="BD">Twice Daily (BD)</option>
                    <option value="TDS">Three Times Daily (TDS)</option>
                    <option value="QID">Four Times Daily (QID)</option>
                    <option value="STAT">Immediately (STAT)</option>
                    <option value="PRN">As Needed (PRN)</option>
                    <option value="NOCTE">At Night (NOCTE)</option>
                    <option value="MANE">In Morning (MANE)</option>
                  </select>
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Duration <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input type="number" class="form-control" v-model="rxDurationDays" min="1" placeholder="Days">
                    <span class="input-group-text">days</span>
                  </div>
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Quantity <span class="text-danger">*</span></label>
                  <input type="number" class="form-control" v-model="rxQuantity" min="1" @change="calculateRxTotal">
                </div>
              </div>
              <div class="row">
                <div class="col-md-3 mb-2">
                  <label class="form-label">Route</label>
                  <select class="form-select" v-model="rxRoute">
                    <option value="ORAL">Oral</option>
                    <option value="IV">Intravenous (IV)</option>
                    <option value="IM">Intramuscular (IM)</option>
                    <option value="SC">Subcutaneous (SC)</option>
                    <option value="TOPICAL">Topical</option>
                    <option value="RECTAL">Rectal</option>
                    <option value="INHALED">Inhaled</option>
                    <option value="SUBLINGUAL">Sublingual</option>
                  </select>
                </div>
                <div class="col-md-6 mb-2">
                  <label class="form-label">Instructions</label>
                  <input type="text" class="form-control" v-model="rxInstructions" placeholder="e.g., Take after meals">
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Total Price</label>
                  <input type="text" class="form-control bg-success text-white fw-bold text-center"
                         :value="'KES ' + numberFormat(rxItemTotal)" readonly>
                </div>
              </div>
              <div class="text-end mt-2">
                <button type="button" class="btn btn-primary" @click="addPrescriptionItem"
                        :disabled="!rxDosage || !rxFrequency || !rxDurationDays || !rxQuantity">
                  <i class="mdi mdi-plus me-1"></i> Add to Prescription
                </button>
              </div>
            </div>

            <!-- Prescription Items List -->
            <div v-if="prescriptionItems.length > 0" class="mb-3">
              <label class="form-label d-flex justify-content-between">
                <span>Prescription Items ({{ prescriptionItems.length }})</span>
                <span class="text-success fw-bold">Total: KES {{ numberFormat(prescriptionTotal) }}</span>
              </label>
              <div class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="table-light">
                  <tr>
                    <th>Medication</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Duration</th>
                    <th class="text-center">Qty</th>
                    <th class="text-end">Unit</th>
                    <th class="text-end">Total</th>
                    <th class="text-center" style="width: 50px;"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in prescriptionItems" :key="index">
                    <td>
                      <strong>{{ item.drugName }}</strong>
                      <small v-if="item.route" class="text-muted d-block">{{ item.route }}</small>
                    </td>
                    <td>{{ item.dosage }}</td>
                    <td>{{ item.frequency }}</td>
                    <td>{{ item.durationDays }} days</td>
                    <td class="text-center">{{ item.quantity }}</td>
                    <td class="text-end">{{ numberFormat(item.unitPrice) }}</td>
                    <td class="text-end fw-bold">{{ numberFormat(item.totalPrice) }}</td>
                    <td class="text-center">
                      <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removePrescriptionItem(index)">
                        <i class="mdi mdi-close-circle"></i>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-success">
                  <tr>
                    <th colspan="6">Grand Total</th>
                    <th class="text-end">KES {{ numberFormat(prescriptionTotal) }}</th>
                    <th></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- No items message -->
            <div v-else class="alert alert-light text-center mb-3">
              <i class="mdi mdi-pill font-size-24 d-block mb-2 text-muted"></i>
              <span class="text-muted">Select a medication and add to prescription</span>
            </div>

            <hr>

            <!-- Additional Notes -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Allergies</label>
                <input type="text" class="form-control" v-model="rxAllergies" placeholder="Known allergies...">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Prescription Notes</label>
                <textarea class="form-control" rows="2" v-model="rxNotes" placeholder="Additional instructions..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="me-auto" v-if="prescriptionItems.length > 0">
              <strong class="text-success font-size-16">Total: KES {{ numberFormat(prescriptionTotal) }}</strong>
            </div>
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-success" @click="submitPrescription"
                    :disabled="prescriptionItems.length === 0 || submittingRx">
              <span v-if="submittingRx"><i class="mdi mdi-loading mdi-spin me-1"></i> Submitting...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Submit Prescription ({{ prescriptionItems.length }} items)</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Radiology Request Modal -->
    <div class="modal fade" id="radiologyModal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title"><i class="mdi mdi-radiology-box me-2"></i>Request Radiology / Imaging</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Select Modality/Category -->
            <div class="mb-3">
              <label class="form-label fw-bold">1. Select Modality/Category <span class="text-danger">*</span></label>
              <select class="form-select" v-model="radSelectedIncomeType" @change="onRadIncomeTypeChange">
                <option value="">-- Select Modality --</option>
                <option v-for="type in radIncomeTypes" :key="type.incomeTypeId" :value="type.incomeTypeId">
                  {{ type.incomeTypeDescription }}
                </option>
              </select>
            </div>

            <!-- Step 2: Select Exam/Procedure -->
            <div class="mb-3" v-if="radSelectedIncomeType">
              <label class="form-label fw-bold">2. Select Examination <span class="text-danger">*</span></label>
              <div v-if="loadingRadExams" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-warning"></div>
                <small class="ms-2">Loading examinations...</small>
              </div>
              <select v-else class="form-select" v-model="radSelectedExam" @change="onRadExamChange">
                <option value="">-- Select Examination --</option>
                <option v-for="exam in radExams" :key="exam.feeId" :value="exam.feeId">
                  {{ exam.feeDescription }} - KES {{ Number(exam.unitFeeAmount).toLocaleString() }}
                </option>
              </select>
            </div>

            <!-- Step 3: Additional Details -->
            <div v-if="radSelectedExamData" class="border rounded p-3 mb-3 bg-light">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Body Part</label>
                    <input type="text" class="form-control" v-model="radBodyPart" placeholder="e.g., Chest, Abdomen, Left Knee">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Laterality</label>
                    <select class="form-select" v-model="radLaterality">
                      <option value="">N/A</option>
                      <option value="Left">Left</option>
                      <option value="Right">Right</option>
                      <option value="Bilateral">Bilateral</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Clinical Indication <span class="text-danger">*</span></label>
                <textarea class="form-control" rows="2" v-model="radClinicalIndication"
                          placeholder="Why is this exam needed? (e.g., Rule out pneumonia, Evaluate for fracture)"></textarea>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Contrast Required?</label>
                    <select class="form-select" v-model="radContrastRequired">
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <select class="form-select" v-model="radPriority">
                      <option value="Routine">Routine</option>
                      <option value="Urgent">Urgent</option>
                      <option value="STAT">STAT (Emergency)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Special Instructions</label>
                <textarea class="form-control" rows="2" v-model="radInstructions"
                          placeholder="Any special instructions for the radiologist or patient preparation"></textarea>
              </div>

              <!-- Price Display -->
              <div class="alert alert-warning mb-0">
                <div class="d-flex justify-content-between align-items-center">
                  <span><strong>{{ radSelectedExamData.feeDescription }}</strong></span>
                  <span class="fs-5 fw-bold">KES {{ Number(radSelectedExamData.unitFeeAmount).toLocaleString() }}</span>
                </div>
              </div>
            </div>

            <!-- Radiology Requests List -->
            <div v-if="radiologyRequests.length > 0" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-bold text-warning">
                  <i class="mdi mdi-radiology-box me-1"></i>Radiology Requests ({{ radiologyRequests.length }})
                </span>
                <span class="badge bg-warning text-dark">Total: KES {{ radiologyRequestsTotal.toLocaleString() }}</span>
              </div>
              <div class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="table-light">
                  <tr>
                    <th>Examination</th>
                    <th>Body Part</th>
                    <th>Priority</th>
                    <th class="text-end">Amount</th>
                    <th width="40"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in radiologyRequests" :key="index">
                    <td>{{ item.examName }}</td>
                    <td>{{ item.bodyPart || '-' }} {{ item.laterality ? '(' + item.laterality + ')' : '' }}</td>
                    <td>
                        <span class="badge" :class="item.priority === 'STAT' ? 'bg-danger' : (item.priority === 'Urgent' ? 'bg-warning text-dark' : 'bg-secondary')">
                          {{ item.priority }}
                        </span>
                    </td>
                    <td class="text-end">{{ Number(item.amount).toLocaleString() }}</td>
                    <td class="text-center">
                      <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removeRadiologyRequest(index)">
                        <i class="mdi mdi-close"></i>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-warning" @click="addRadiologyRequest"
                    :disabled="!radSelectedExamData || !radClinicalIndication">
              <i class="mdi mdi-plus me-1"></i> Add Request
            </button>
            <button type="button" class="btn btn-success" @click="submitRadiologyRequests"
                    :disabled="radiologyRequests.length === 0 || submittingRad">
              <span v-if="submittingRad"><i class="mdi mdi-loading mdi-spin me-1"></i> Submitting...</span>
              <span v-else><i class="mdi mdi-send me-1"></i> Submit Requests ({{ radiologyRequests.length }})</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Surgery Request Modal -->
    <div class="modal fade" id="surgeryModal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title"><i class="mdi mdi-hospital-box me-2"></i>Request Surgery</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Select Surgery Category -->
            <div class="mb-3">
              <label class="form-label fw-bold">1. Select Surgery Category <span class="text-danger">*</span></label>
              <select class="form-select" v-model="surgSelectedIncomeType" @change="onSurgIncomeTypeChange">
                <option value="">-- Select Category --</option>
                <option v-for="type in surgIncomeTypes" :key="type.incomeTypeId" :value="type.incomeTypeId">
                  {{ type.incomeTypeDescription }}
                </option>
              </select>
            </div>

            <!-- Step 2: Select Procedure -->
            <div class="mb-3" v-if="surgSelectedIncomeType">
              <label class="form-label fw-bold">2. Select Procedure <span class="text-danger">*</span></label>
              <div v-if="loadingSurgProcedures" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-danger"></div>
                <small class="ms-2">Loading procedures...</small>
              </div>
              <select v-else class="form-select" v-model="surgSelectedProcedure" @change="onSurgProcedureChange">
                <option value="">-- Select Procedure --</option>
                <option v-for="proc in surgProcedures" :key="proc.feeId" :value="proc.feeId">
                  {{ proc.feeDescription }} - KES {{ Number(proc.unitFeeAmount).toLocaleString() }}
                </option>
              </select>
            </div>

            <!-- Step 3: Surgery Details -->
            <div v-if="surgSelectedProcedureData" class="border rounded p-3 mb-3 bg-light">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Surgery Type <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="surgType">
                    <option value="Elective">Elective</option>
                    <option value="Emergency">Emergency</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Surgery Category</label>
                  <select class="form-select" v-model="surgCategory">
                    <option value="">-- Select --</option>
                    <option value="Major">Major</option>
                    <option value="Minor">Minor</option>
                    <option value="Day Case">Day Case</option>
                  </select>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Body Part</label>
                  <input type="text" class="form-control" v-model="surgBodyPart" placeholder="e.g., Abdomen, Right Knee">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Laterality</label>
                  <select class="form-select" v-model="surgLaterality">
                    <option value="">N/A</option>
                    <option value="Left">Left</option>
                    <option value="Right">Right</option>
                    <option value="Bilateral">Bilateral</option>
                  </select>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Diagnosis / Indication <span class="text-danger">*</span></label>
                <textarea class="form-control" rows="2" v-model="surgDiagnosis"
                          placeholder="Why is surgery needed?"></textarea>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Scheduled Date</label>
                  <input type="date" class="form-control" v-model="surgScheduledDate">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Scheduled Time</label>
                  <input type="time" class="form-control" v-model="surgScheduledTime">
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Estimated Duration (minutes)</label>
                  <input type="number" class="form-control" v-model="surgEstimatedDuration" min="15" step="15" placeholder="60">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Priority</label>
                  <select class="form-select" v-model="surgPriority">
                    <option value="Routine">Routine</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Anesthesia Type</label>
                  <select class="form-select" v-model="surgAnesthesiaType">
                    <option value="">-- Select --</option>
                    <option value="General">General Anesthesia</option>
                    <option value="Spinal">Spinal Anesthesia</option>
                    <option value="Local">Local Anesthesia</option>
                    <option value="Regional">Regional Block</option>
                    <option value="Sedation">Sedation</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">ASA Risk Classification</label>
                  <select class="form-select" v-model="surgAnesthesiaRisk">
                    <option value="">-- Select --</option>
                    <option value="ASA I">ASA I - Healthy</option>
                    <option value="ASA II">ASA II - Mild systemic disease</option>
                    <option value="ASA III">ASA III - Severe systemic disease</option>
                    <option value="ASA IV">ASA IV - Life-threatening disease</option>
                    <option value="ASA V">ASA V - Moribund</option>
                  </select>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Blood Required?</label>
                  <select class="form-select" v-model="surgBloodRequired">
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3" v-if="surgBloodRequired === 'Yes'">
                  <label class="form-label">Number of Units</label>
                  <input type="number" class="form-control" v-model="surgBloodUnits" min="1" placeholder="Units">
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Pre-Op Instructions</label>
                <textarea class="form-control" rows="2" v-model="surgPreOpInstructions"
                          placeholder="NPO from midnight, lab tests required, etc."></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label">Special Equipment Needed</label>
                <input type="text" class="form-control" v-model="surgSpecialEquipment" placeholder="e.g., Laparoscopic set, C-arm">
              </div>

              <!-- Price Display -->
              <div class="alert alert-danger mb-0">
                <div class="d-flex justify-content-between align-items-center">
                  <span><strong>{{ surgSelectedProcedureData.feeDescription }}</strong></span>
                  <span class="fs-5 fw-bold">KES {{ Number(surgSelectedProcedureData.unitFeeAmount).toLocaleString() }}</span>
                </div>
              </div>
            </div>

            <!-- No procedure selected message -->
            <div v-if="!surgSelectedProcedureData" class="alert alert-light text-center mb-3">
              <i class="mdi mdi-hospital-box font-size-24 d-block mb-2 text-muted"></i>
              <span class="text-muted">Select a category and procedure to continue</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="submitSurgeryRequest"
                    :disabled="!surgSelectedProcedureData || !surgDiagnosis || submittingSurg">
              <span v-if="submittingSurg"><i class="mdi mdi-loading mdi-spin me-1"></i> Submitting...</span>
              <span v-else><i class="mdi mdi-send me-1"></i> Submit Surgery Request</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital, biller } from "@/api";

export default {
  name: "Consultation",
  components: { NavigationBar, ToastNotification },
  props: {
    visitID: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      currentVisitID: '',
      visit: {},
      triage: null,
      existingConsultation: null,
      activeTab: 'history',
      consultationStartTime: null,

      consultation: {
        chiefComplaint: '',
        historyOfPresentIllness: '',
        pastMedicalHistory: '',
        familyHistory: '',
        socialHistory: '',
        reviewOfSystems: '',
        physicalExamination: '',
        systemicExamination: '',
        provisionalDiagnosis: '',
        differentialDiagnosis: '',
        finalDiagnosis: '',
        treatmentPlan: '',
        investigationsOrdered: '',
        proceduresPerformed: '',
        patientEducation: '',
        doctorNotes: '',
        followUpRequired: 'No',
        followUpDate: '',
        followUpNotes: '',
        referralRequired: 'No',
        referralTo: '',
        admissionRequired: 'No',
        admissionWard: '',
        sickOffDays: 0
      },

      // ICD-10
      icdSearch: '',
      icdResults: [],
      selectedICDs: [],

      // Lab Request
      labTests: [],
      labSearchTerm: '',
      selectedLabTests: [],
      labPriority: 'ROUTINE',
      labClinicalNotes: '',
      submittingLab: false,

      // Lab Request - Biller Integration
      labIncomeTypes: [],
      labFeesAndCharges: [],
      labSelectedIncomeType: '',
      labSelectedFee: '',
      labSelectedFeeData: null,
      loadingLabFees: false,

      // Lab Results (for viewing)
      labResults: [],
      loadingLabResults: false,

      // Radiology Results (for viewing)
      radiologyResults: [],
      loadingRadResults: false,

      // Surgery Results (for viewing)
      surgeryResults: [],
      loadingSurgResults: false,

      // Prescription - Biller Integration
      rxIncomeTypes: [],
      rxFeesAndCharges: [],
      rxSelectedIncomeType: '',
      rxSelectedFee: '',
      rxSelectedFeeData: null,
      loadingRxFees: false,

      // Prescription Form Fields
      rxDosage: '',
      rxFrequency: '',
      rxDurationDays: '',
      rxQuantity: 1,
      rxRoute: 'ORAL',
      rxInstructions: '',
      rxAllergies: '',
      rxNotes: '',

      // Prescription Items List
      prescriptionItems: [],
      submittingRx: false,

      // Radiology - Biller Integration
      radIncomeTypes: [],
      radExams: [],
      radSelectedIncomeType: '',
      radSelectedExam: '',
      radSelectedExamData: null,
      loadingRadExams: false,

      // Radiology Form Fields
      radBodyPart: '',
      radLaterality: '',
      radClinicalIndication: '',
      radContrastRequired: 'No',
      radPriority: 'Routine',
      radInstructions: '',

      // Radiology Requests List
      radiologyRequests: [],
      submittingRad: false,

      // Surgery - Biller Integration
      surgIncomeTypes: [],
      surgProcedures: [],
      surgSelectedIncomeType: '',
      surgSelectedProcedure: '',
      surgSelectedProcedureData: null,
      loadingSurgProcedures: false,

      // Surgery Form Fields
      surgType: 'Elective',
      surgCategory: '',
      surgBodyPart: '',
      surgLaterality: '',
      surgDiagnosis: '',
      surgScheduledDate: '',
      surgScheduledTime: '',
      surgEstimatedDuration: 60,
      surgPriority: 'Routine',
      surgAnesthesiaType: '',
      surgAnesthesiaRisk: '',
      surgBloodRequired: 'No',
      surgBloodUnits: 0,
      surgPreOpInstructions: '',
      surgSpecialEquipment: '',
      submittingSurg: false,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      doctorID: ''
    };
  },
  computed: {
    canSubmit() {
      return this.consultation.chiefComplaint &&
          this.consultation.physicalExamination &&
          this.consultation.provisionalDiagnosis &&
          this.consultation.treatmentPlan;
    },
    filteredLabTests() {
      if (!this.labSearchTerm) return this.labTests.slice(0, 20);
      const search = this.labSearchTerm.toLowerCase();
      return this.labTests.filter(t =>
          t.testName.toLowerCase().includes(search) ||
          (t.testCategory && t.testCategory.toLowerCase().includes(search))
      );
    },
    labOrderTotal() {
      return this.selectedLabTests.reduce((sum, test) => sum + (parseFloat(test.unitPrice) || 0), 0);
    },
    rxItemTotal() {
      if (!this.rxSelectedFeeData || !this.rxQuantity) return 0;
      return parseFloat(this.rxSelectedFeeData.unitFeeAmount) * parseInt(this.rxQuantity);
    },
    prescriptionTotal() {
      return this.prescriptionItems.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
    },
    radiologyRequestsTotal() {
      return this.radiologyRequests.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    // ==========================================
    // SURGERY RESULTS METHODS
    // ==========================================

    loadSurgeryResults() {
      if (!this.currentVisitID) return;

      this.loadingSurgResults = true;
      const data = new FormData();
      data.append("function", "getSurgeryRequestsByVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loadingSurgResults = false;
            if (res.data.success) {
              this.surgeryResults = res.data.data.requests || [];
            }
          })
          .catch((e) => {
            this.loadingSurgResults = false;
            console.error(e);
          });
    },

    getSurgeryHeaderClass(status) {
      switch (status) {
        case 'Completed': return 'bg-success text-white';
        case 'In Theatre': return 'bg-danger text-white';
        case 'Pre-Op': return 'bg-info text-white';
        case 'Scheduled': return 'bg-primary text-white';
        case 'Cancelled':
        case 'Postponed': return 'bg-secondary text-white';
        default: return 'bg-warning text-dark';
      }
    },

    getSurgeryStatusBadge(status) {
      switch (status) {
        case 'Completed': return 'bg-light text-success';
        case 'In Theatre': return 'bg-light text-danger';
        case 'Pre-Op': return 'bg-light text-info';
        case 'Scheduled': return 'bg-light text-primary';
        case 'Cancelled':
        case 'Postponed': return 'bg-light text-secondary';
        default: return 'bg-light text-warning';
      }
    },

    getSurgeryPriorityBadge(priority) {
      switch (priority) {
        case 'Emergency': return 'bg-danger';
        case 'Urgent': return 'bg-warning text-dark';
        default: return 'bg-secondary';
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatTime(time) {
      if (!time) return '-';
      if (time.includes('T')) {
        return new Date(time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      }
      return time.substring(0, 5);
    },

    calculateDuration(start, end) {
      if (!start || !end) return '-';
      const startTime = new Date(start);
      const endTime = new Date(end);
      const diffMs = endTime - startTime;
      const diffMins = Math.round(diffMs / 60000);
      if (diffMins < 60) return `${diffMins} min`;
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h ${mins}m`;
    },

    printOperativeNotes(surg) {
      const printContent = `
        <html>
        <head>
          <title>Operative Notes - ${surg.requestID}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 16px; text-align: center; margin-bottom: 5px; }
            h2 { font-size: 14px; text-align: center; color: #666; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .header-table { width: 100%; margin-bottom: 15px; }
            .header-table td { padding: 3px 5px; }
            .section { margin-bottom: 12px; }
            .section-title { font-weight: bold; color: #333; margin-bottom: 3px; background: #f0f0f0; padding: 5px; }
            .section-content { padding: 5px; border: 1px solid #ddd; min-height: 20px; white-space: pre-wrap; }
            .highlight { background: #e8f5e9; border-color: #4caf50; }
            .warning { background: #fff3e0; border-color: #ff9800; }
            .two-col { display: flex; gap: 10px; }
            .two-col > div { flex: 1; }
            .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 15px; }
            .signature-line { margin-top: 40px; border-top: 1px solid #333; width: 200px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>${this.facilityName || 'County Hospital'}</h1>
          <h2>OPERATIVE NOTES</h2>

          <table class="header-table">
            <tr>
              <td><strong>Patient:</strong> ${this.visit.patientName}</td>
              <td><strong>Patient ID:</strong> ${this.visit.patientID}</td>
              <td><strong>Age/Gender:</strong> ${this.visit.patientAge || '-'} / ${this.visit.patientGender || '-'}</td>
            </tr>
            <tr>
              <td><strong>Surgery Date:</strong> ${this.formatDate(surg.scheduledDate)}</td>
              <td><strong>Theatre:</strong> ${surg.theatreRoom || '-'}</td>
              <td><strong>Request ID:</strong> ${surg.requestID}</td>
            </tr>
          </table>

          <div class="section">
            <div class="section-title">PROCEDURE</div>
            <div class="section-content">${surg.procedureName}</div>
          </div>

          <div class="two-col">
            <div class="section">
              <div class="section-title">SURGEON</div>
              <div class="section-content">${surg.surgeonName || '-'}</div>
            </div>
            <div class="section">
              <div class="section-title">ANESTHETIST</div>
              <div class="section-content">${surg.anesthetistName || '-'}</div>
            </div>
          </div>

          <div class="two-col">
            <div class="section">
              <div class="section-title">ASSISTANT SURGEON</div>
              <div class="section-content">${surg.assistantSurgeonName || '-'}</div>
            </div>
            <div class="section">
              <div class="section-title">ANESTHESIA TYPE</div>
              <div class="section-content">${surg.anesthesiaType || '-'}</div>
            </div>
          </div>

          <div class="two-col">
            <div class="section">
              <div class="section-title">SURGERY START</div>
              <div class="section-content">${surg.surgeryStartTime ? this.formatTime(surg.surgeryStartTime) : '-'}</div>
            </div>
            <div class="section">
              <div class="section-title">SURGERY END</div>
              <div class="section-content">${surg.surgeryEndTime ? this.formatTime(surg.surgeryEndTime) : '-'}</div>
            </div>
            <div class="section">
              <div class="section-title">DURATION</div>
              <div class="section-content">${this.calculateDuration(surg.surgeryStartTime, surg.surgeryEndTime)}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">PRE-OPERATIVE DIAGNOSIS</div>
            <div class="section-content">${surg.diagnosis || '-'}</div>
          </div>

          <div class="section">
            <div class="section-title">POST-OPERATIVE DIAGNOSIS</div>
            <div class="section-content highlight">${surg.postOpDiagnosis || surg.diagnosis || '-'}</div>
          </div>

          <div class="section">
            <div class="section-title">OPERATIVE FINDINGS</div>
            <div class="section-content">${surg.operativeFindings || '-'}</div>
          </div>

          <div class="section">
            <div class="section-title">PROCEDURE DETAILS</div>
            <div class="section-content">${surg.procedureDetails || '-'}</div>
          </div>

          ${surg.complications ? `
          <div class="section">
            <div class="section-title">COMPLICATIONS</div>
            <div class="section-content warning">${surg.complications}</div>
          </div>
          ` : ''}

          <div class="two-col">
            <div class="section">
              <div class="section-title">BLOOD LOSS</div>
              <div class="section-content">${surg.bloodLoss ? surg.bloodLoss + ' ml' : '-'}</div>
            </div>
            <div class="section">
              <div class="section-title">SPECIMENS</div>
              <div class="section-content">${surg.specimens || 'None'}</div>
            </div>
          </div>

          <div class="two-col">
            <div class="section">
              <div class="section-title">DRAINS</div>
              <div class="section-content">${surg.drains || 'None'}</div>
            </div>
            <div class="section">
              <div class="section-title">IMPLANTS</div>
              <div class="section-content">${surg.implants || 'None'}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">POST-OP INSTRUCTIONS</div>
            <div class="section-content">${surg.postOpInstructions || '-'}</div>
          </div>

          <div class="two-col">
            <div class="section">
              <div class="section-title">POST-OP DESTINATION</div>
              <div class="section-content">${surg.postOpDestination || 'Ward'}</div>
            </div>
            <div class="section">
              <div class="section-title">COUNTS VERIFIED</div>
              <div class="section-content">
                Swabs: ${surg.swabCountCorrect === 'Yes' ? '✓' : '-'} |
                Instruments: ${surg.instrumentCountCorrect === 'Yes' ? '✓' : '-'} |
                Needles: ${surg.needleCountCorrect === 'Yes' ? '✓' : '-'}
              </div>
            </div>
          </div>

          <div class="footer">
            <p><strong>Surgeon's Signature:</strong></p>
            <div class="signature-line"></div>
            <p>${surg.surgeonName || '-'}</p>
            <p><small>Date: ${this.formatDate(surg.completedDate || surg.dateModified)}</small></p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    },

    // ==========================================
    // SURGERY MODAL METHODS
    // ==========================================

    showSurgeryModal() {
      if (this.surgIncomeTypes.length === 0) {
        this.loadSurgIncomeTypes();
      }
      this.resetSurgeryForm();
      const modal = new bootstrap.Modal(document.getElementById('surgeryModal'));
      modal.show();
    },

    loadSurgIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              const allTypes = res.data.data.incomeTypes || [];
              this.surgIncomeTypes = allTypes.filter(type => {
                const desc = (type.incomeTypeDescription || '').toLowerCase();
                return desc.includes('surg') || desc.includes('theatre') || desc.includes('theater') ||
                    desc.includes('operation') || desc.includes('procedure') || desc.includes('minor') ||
                    desc.includes('major') || desc.includes('ortho') || desc.includes('gynae') ||
                    desc.includes('obstetric') || desc.includes('caesar');
              });
              if (this.surgIncomeTypes.length === 0) {
                this.surgIncomeTypes = allTypes;
              }
            }
          })
          .catch((e) => console.error("Error loading surgery income types:", e));
    },

    onSurgIncomeTypeChange() {
      this.surgSelectedProcedure = '';
      this.surgSelectedProcedureData = null;
      this.surgProcedures = [];
      if (this.surgSelectedIncomeType) {
        this.loadSurgProcedures();
      }
    },

    loadSurgProcedures() {
      this.loadingSurgProcedures = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeID", this.surgSelectedIncomeType);

      execute(data, biller)
          .then((res) => {
            this.loadingSurgProcedures = false;
            if (res.data.success) {
              this.surgProcedures = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingSurgProcedures = false;
            console.error("Error loading surgery procedures:", e);
          });
    },

    onSurgProcedureChange() {
      if (this.surgSelectedProcedure) {
        this.surgSelectedProcedureData = this.surgProcedures.find(p => p.feeId == this.surgSelectedProcedure);
      } else {
        this.surgSelectedProcedureData = null;
      }
    },

    resetSurgeryForm() {
      this.surgSelectedIncomeType = '';
      this.surgSelectedProcedure = '';
      this.surgSelectedProcedureData = null;
      this.surgProcedures = [];
      this.surgType = 'Elective';
      this.surgCategory = '';
      this.surgBodyPart = '';
      this.surgLaterality = '';
      this.surgDiagnosis = '';
      this.surgScheduledDate = '';
      this.surgScheduledTime = '';
      this.surgEstimatedDuration = 60;
      this.surgPriority = 'Routine';
      this.surgAnesthesiaType = '';
      this.surgAnesthesiaRisk = '';
      this.surgBloodRequired = 'No';
      this.surgBloodUnits = 0;
      this.surgPreOpInstructions = '';
      this.surgSpecialEquipment = '';
    },

    submitSurgeryRequest() {
      if (!this.surgSelectedProcedureData || !this.surgDiagnosis) {
        this.showError('Please select a procedure and provide diagnosis');
        return;
      }

      this.submittingSurg = true;

      const data = new FormData();
      data.append("function", "addSurgeryRequest");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("patientAge", this.visit.patientAge || '');
      data.append("patientGender", this.visit.patientGender || '');
      data.append("phoneNumber", this.visit.phoneNumber || '');

      data.append("procedureName", this.surgSelectedProcedureData.feeDescription);
      data.append("procedureCode", this.surgSelectedProcedureData.feeId);
      data.append("surgeryType", this.surgType);
      data.append("surgeryCategory", this.surgCategory);
      data.append("bodyPart", this.surgBodyPart);
      data.append("laterality", this.surgLaterality);
      data.append("diagnosis", this.surgDiagnosis);

      data.append("scheduledDate", this.surgScheduledDate);
      data.append("scheduledTime", this.surgScheduledTime);
      data.append("estimatedDuration", this.surgEstimatedDuration);
      data.append("priority", this.surgPriority);

      data.append("anesthesiaType", this.surgAnesthesiaType);
      data.append("anesthesiaRisk", this.surgAnesthesiaRisk);

      data.append("preOpInstructions", this.surgPreOpInstructions);
      data.append("bloodRequired", this.surgBloodRequired);
      data.append("bloodUnits", this.surgBloodUnits);
      data.append("specialEquipment", this.surgSpecialEquipment);

      data.append("amount", this.surgSelectedProcedureData.unitFeeAmount);
      data.append("billerFeeID", this.surgSelectedProcedureData.feeId);
      data.append("billerIncomeTypeID", this.surgSelectedIncomeType);

      data.append("requestingDoctorID", this.doctorID);
      data.append("requestingDoctorName", this.sessionNames);

      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.submittingSurg = false;
            if (res.data.success) {
              this.showSuccess('Surgery request submitted successfully');

              const surgNote = `Surgery Requested: ${this.surgSelectedProcedureData.feeDescription}` +
                  (this.surgBodyPart ? ` (${this.surgBodyPart})` : '') +
                  ` - ${this.surgDiagnosis}`;

              if (this.consultation.treatmentPlan) {
                this.consultation.treatmentPlan += '\n\n' + surgNote;
              } else {
                this.consultation.treatmentPlan = surgNote;
              }

              this.resetSurgeryForm();
              bootstrap.Modal.getInstance(document.getElementById('surgeryModal')).hide();

              // Reload surgery results
              this.loadSurgeryResults();
            } else {
              this.showError(res.data.message || 'Failed to submit surgery request');
            }
          })
          .catch((e) => {
            this.submittingSurg = false;
            console.error("Error submitting surgery request:", e);
            this.showError('Error submitting surgery request');
          });
    },

    // ==========================================
    // PRESCRIPTION MODAL METHODS
    // ==========================================

    showPrescriptionModal() {
      if (this.rxIncomeTypes.length === 0) {
        this.loadRxIncomeTypes();
      }
      this.resetPrescriptionForm();

      const modal = new bootstrap.Modal(document.getElementById('prescriptionModal'));
      modal.show();
    },

    loadRxIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              const allTypes = res.data.data.incomeTypes || [];
              this.rxIncomeTypes = allTypes.filter(t => {
                const desc = (t.incomeTypeDescription || '').toLowerCase();
                return desc.includes('pharm') ||
                    desc.includes('drug') ||
                    desc.includes('medicine') ||
                    desc.includes('medication') ||
                    desc.includes('dispensing');
              });
              if (this.rxIncomeTypes.length === 0) {
                this.rxIncomeTypes = allTypes;
              }
            }
          })
          .catch((e) => console.error("Error loading rx income types:", e));
    },

    onRxIncomeTypeChange() {
      this.rxFeesAndCharges = [];
      this.rxSelectedFee = '';
      this.rxSelectedFeeData = null;

      if (this.rxSelectedIncomeType) {
        this.loadRxFeesAndCharges(this.rxSelectedIncomeType);
      }
    },

    loadRxFeesAndCharges(incomeTypeId) {
      this.loadingRxFees = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingRxFees = false;
            if (res.data.success) {
              this.rxFeesAndCharges = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingRxFees = false;
            console.error("Error loading rx fees:", e);
          });
    },

    onRxFeeChange() {
      if (this.rxSelectedFee) {
        this.rxSelectedFeeData = this.rxFeesAndCharges.find(f => f.feeId == this.rxSelectedFee);
        this.rxQuantity = 1;
      } else {
        this.rxSelectedFeeData = null;
      }
    },

    calculateRxTotal() {
      // Triggered on quantity change - computed handles the rest
    },

    resetPrescriptionForm() {
      this.rxSelectedFee = '';
      this.rxSelectedFeeData = null;
      this.rxDosage = '';
      this.rxFrequency = '';
      this.rxDurationDays = '';
      this.rxQuantity = 1;
      this.rxRoute = 'ORAL';
      this.rxInstructions = '';
    },

    addPrescriptionItem() {
      if (!this.rxSelectedFeeData || !this.rxDosage || !this.rxFrequency || !this.rxDurationDays || !this.rxQuantity) {
        this.showError('Please fill in all required fields');
        return;
      }

      const incomeType = this.rxIncomeTypes.find(t => t.incomeTypeId == this.rxSelectedIncomeType);

      const item = {
        drugName: this.rxSelectedFeeData.feeDescription,
        drugCode: this.rxSelectedFeeData.feeId,
        dosage: this.rxDosage,
        frequency: this.rxFrequency,
        durationDays: parseInt(this.rxDurationDays),
        quantity: parseInt(this.rxQuantity),
        route: this.rxRoute,
        instructions: this.rxInstructions,
        unitPrice: parseFloat(this.rxSelectedFeeData.unitFeeAmount),
        totalPrice: this.rxItemTotal,
        billerFeeID: this.rxSelectedFeeData.feeId,
        billerIncomeTypeID: this.rxSelectedIncomeType,
        incomeTypeDesc: incomeType?.incomeTypeDescription || ''
      };

      this.prescriptionItems.push(item);
      this.resetPrescriptionForm();
      this.showSuccess('Medication added to prescription');
    },

    removePrescriptionItem(index) {
      this.prescriptionItems.splice(index, 1);
    },

    submitPrescription() {
      if (this.prescriptionItems.length === 0) {
        this.showError('Please add at least one medication');
        return;
      }

      this.submittingRx = true;
      const medicationNames = this.prescriptionItems.map(item => item.drugName).join(', ');

      const data = new FormData();
      data.append("function", "addPrescription");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("patientAge", this.visit.patientAge || '');
      data.append("prescriberID", this.doctorID);
      data.append("prescriberName", this.sessionNames);
      data.append("diagnosis", this.consultation.finalDiagnosis || this.consultation.provisionalDiagnosis || '');
      data.append("allergies", this.rxAllergies);
      data.append("prescriptionNotes", this.rxNotes);
      data.append("items", JSON.stringify(this.prescriptionItems));
      data.append("totalAmount", this.prescriptionTotal);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.submittingRx = false;
            if (res.data.success) {
              this.showSuccess('Prescription submitted successfully');

              if (this.consultation.treatmentPlan) {
                this.consultation.treatmentPlan += '\n\nPrescribed: ' + medicationNames;
              } else {
                this.consultation.treatmentPlan = 'Prescribed: ' + medicationNames;
              }

              this.prescriptionItems = [];
              this.rxAllergies = '';
              this.rxNotes = '';

              bootstrap.Modal.getInstance(document.getElementById('prescriptionModal')).hide();
            } else {
              this.showError(res.data.message || 'Failed to submit prescription');
            }
          })
          .catch((e) => {
            this.submittingRx = false;
            console.error("Error submitting prescription:", e);
            this.showError('Error submitting prescription');
          });
    },

    // ==========================================
    // RADIOLOGY MODAL METHODS
    // ==========================================

    showRadiologyModal() {
      if (this.radIncomeTypes.length === 0) {
        this.loadRadIncomeTypes();
      }
      this.resetRadiologyForm();
      const modal = new bootstrap.Modal(document.getElementById('radiologyModal'));
      modal.show();
    },

    loadRadIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              const allTypes = res.data.data.incomeTypes || [];
              this.radIncomeTypes = allTypes.filter(type => {
                const desc = (type.incomeTypeDescription || '').toLowerCase();
                return desc.includes('radiology') || desc.includes('x-ray') || desc.includes('xray') ||
                    desc.includes('imaging') || desc.includes('ultrasound') || desc.includes('scan') ||
                    desc.includes('ct') || desc.includes('mri') || desc.includes('mammograph') ||
                    desc.includes('fluoroscopy') || desc.includes('echo');
              });
              if (this.radIncomeTypes.length === 0) {
                this.radIncomeTypes = allTypes;
              }
            }
          })
          .catch((e) => console.error("Error loading radiology income types:", e));
    },

    onRadIncomeTypeChange() {
      this.radSelectedExam = '';
      this.radSelectedExamData = null;
      this.radExams = [];
      if (this.radSelectedIncomeType) {
        this.loadRadExams();
      }
    },

    loadRadExams() {
      this.loadingRadExams = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeID", this.radSelectedIncomeType);

      execute(data, biller)
          .then((res) => {
            this.loadingRadExams = false;
            if (res.data.success) {
              this.radExams = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingRadExams = false;
            console.error("Error loading radiology exams:", e);
          });
    },

    onRadExamChange() {
      if (this.radSelectedExam) {
        this.radSelectedExamData = this.radExams.find(e => e.feeId == this.radSelectedExam);
      } else {
        this.radSelectedExamData = null;
      }
    },

    resetRadiologyForm() {
      this.radSelectedIncomeType = '';
      this.radSelectedExam = '';
      this.radSelectedExamData = null;
      this.radExams = [];
      this.radBodyPart = '';
      this.radLaterality = '';
      this.radClinicalIndication = '';
      this.radContrastRequired = 'No';
      this.radPriority = 'Routine';
      this.radInstructions = '';
    },

    addRadiologyRequest() {
      if (!this.radSelectedExamData || !this.radClinicalIndication) {
        this.showError('Please select an examination and provide clinical indication');
        return;
      }

      const request = {
        examName: this.radSelectedExamData.feeDescription,
        examCode: this.radSelectedExamData.feeId,
        modalityType: this.radIncomeTypes.find(t => t.incomeTypeId == this.radSelectedIncomeType)?.incomeTypeDescription || '',
        bodyPart: this.radBodyPart,
        laterality: this.radLaterality,
        clinicalIndication: this.radClinicalIndication,
        contrastRequired: this.radContrastRequired,
        priority: this.radPriority,
        instructions: this.radInstructions,
        amount: parseFloat(this.radSelectedExamData.unitFeeAmount) || 0,
        billerFeeID: this.radSelectedExamData.feeId,
        billerIncomeTypeID: this.radSelectedIncomeType,
        incomeTypeDesc: this.radIncomeTypes.find(t => t.incomeTypeId == this.radSelectedIncomeType)?.incomeTypeDescription || ''
      };

      this.radiologyRequests.push(request);

      this.radSelectedExam = '';
      this.radSelectedExamData = null;
      this.radBodyPart = '';
      this.radLaterality = '';
      this.radClinicalIndication = '';
      this.radContrastRequired = 'No';
      this.radPriority = 'Routine';
      this.radInstructions = '';

      this.showSuccess('Radiology request added');
    },

    removeRadiologyRequest(index) {
      this.radiologyRequests.splice(index, 1);
    },

    submitRadiologyRequests() {
      if (this.radiologyRequests.length === 0) {
        this.showError('Please add at least one radiology request');
        return;
      }

      this.submittingRad = true;

      const data = new FormData();
      data.append("function", "addRadiologyRequests");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("orderingDoctorID", this.doctorID);
      data.append("orderingDoctorName", this.sessionNames);
      data.append("requests", JSON.stringify(this.radiologyRequests));
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.submittingRad = false;
            if (res.data.success) {
              this.showSuccess(`${this.radiologyRequests.length} radiology request(s) submitted successfully`);

              const radNote = this.radiologyRequests.map(r =>
                  `${r.examName}${r.bodyPart ? ' (' + r.bodyPart + ')' : ''} - ${r.clinicalIndication}`
              ).join('; ');

              if (this.consultation.treatmentPlan) {
                this.consultation.treatmentPlan += '\n\nRadiology Requests: ' + radNote;
              } else {
                this.consultation.treatmentPlan = 'Radiology Requests: ' + radNote;
              }

              this.radiologyRequests = [];
              bootstrap.Modal.getInstance(document.getElementById('radiologyModal')).hide();
            } else {
              this.showError(res.data.message || 'Failed to submit radiology requests');
            }
          })
          .catch((e) => {
            this.submittingRad = false;
            console.error("Error submitting radiology requests:", e);
            this.showError('Error submitting radiology requests');
          });
    },

    // ==========================================
    // HELPER METHODS
    // ==========================================

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    showSuccess(message) {
      if (this.$refs.toast) {
        this.$refs.toast.success(message);
      } else {
        console.log('Success:', message);
      }
    },

    showError(message) {
      if (this.$refs.toast) {
        this.$refs.toast.error(message);
      } else {
        console.error('Error:', message);
        alert(message);
      }
    },

    updateVisitStatus(newStatus) {
      const data = new FormData();
      data.append("function", "updateVisitQueueStatus");
      data.append("visitID", this.currentVisitID);
      data.append("queueStatus", newStatus);
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.visit.queueStatus = newStatus;
            }
          })
          .catch((e) => {
            console.error("Error updating visit status:", e);
          });
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },

    getTriageBadge(category) {
      if (!category) return 'bg-secondary';
      const cat = category.toLowerCase();
      if (cat.includes('emergency')) return 'bg-danger';
      if (cat.includes('urgent')) return 'bg-warning text-dark';
      if (cat.includes('less')) return 'bg-info';
      return 'bg-success';
    },

    // Lab Results Methods
    loadLabResults() {
      if (!this.currentVisitID) return;

      this.loadingLabResults = true;
      const data = new FormData();
      data.append("function", "getLabResultsByVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loadingLabResults = false;
            if (res.data.success) {
              this.labResults = res.data.data.labResults || [];
            }
          })
          .catch((e) => {
            this.loadingLabResults = false;
            console.error(e);
          });
    },

    getLabFlagClass(flag) {
      if (!flag) return '';
      if (flag.includes('Critical')) return 'text-danger';
      if (flag === 'High' || flag === 'Low') return 'text-warning';
      return '';
    },

    getLabFlagBadge(flag) {
      if (!flag) return 'bg-success';
      if (flag.includes('Critical')) return 'bg-danger';
      if (flag === 'High' || flag === 'Low') return 'bg-warning text-dark';
      return 'bg-info';
    },

    getLabStatusBadge(status) {
      switch (status) {
        case 'Pending': return 'bg-warning text-dark';
        case 'Sample Collected': return 'bg-info';
        case 'Completed': return 'bg-success';
        default: return 'bg-secondary';
      }
    },

    formatLabDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },

    viewLabReport(requestID) {
      this.$router.push('/lab-report/' + requestID);
    },

    // Radiology Results Methods
    loadRadiologyResults() {
      if (!this.currentVisitID) return;

      this.loadingRadResults = true;
      const data = new FormData();
      data.append("function", "getRadiologyRequestsByVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loadingRadResults = false;
            if (res.data.success) {
              this.radiologyResults = res.data.data.requests || [];
            }
          })
          .catch((e) => {
            this.loadingRadResults = false;
            console.error(e);
          });
    },

    printRadiologyReport(rad) {
      const printContent = `
        <html>
        <head>
          <title>Radiology Report - ${rad.requestID}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; text-align: center; margin-bottom: 5px; }
            h2 { font-size: 14px; text-align: center; color: #666; margin-bottom: 20px; }
            .section { margin-bottom: 15px; }
            .label { font-weight: bold; color: #333; margin-bottom: 5px; }
            .value { margin-top: 5px; }
            .header-info { display: flex; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
            .findings { background: #f5f5f5; padding: 10px; border-radius: 5px; white-space: pre-wrap; }
            .impression { background: #e8f5e9; padding: 10px; border-radius: 5px; font-weight: bold; white-space: pre-wrap; }
            .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${this.facilityName || 'County Hospital'}</h1>
          <h2>RADIOLOGY REPORT</h2>
          <div class="header-info">
            <div>
              <strong>Patient:</strong> ${this.visit.patientName}<br>
              <strong>Patient ID:</strong> ${this.visit.patientID}<br>
              <strong>Age/Gender:</strong> ${this.visit.patientAge || '-'} / ${this.visit.patientGender || '-'}
            </div>
            <div style="text-align: right;">
              <strong>Request ID:</strong> ${rad.requestID}<br>
              <strong>Visit ID:</strong> ${rad.visitID}<br>
              <strong>Report Date:</strong> ${this.formatDateTime(rad.reportDate)}
            </div>
          </div>
          <div class="section">
            <div class="label">Examination:</div>
            <div class="value">${rad.examName} (${rad.modalityType})</div>
          </div>
          <div class="section">
            <div class="label">Body Part:</div>
            <div class="value">${rad.bodyPart || '-'} ${rad.laterality ? '(' + rad.laterality + ')' : ''}</div>
          </div>
          <div class="section">
            <div class="label">Clinical Indication:</div>
            <div class="value">${rad.clinicalIndication || '-'}</div>
          </div>
          ${rad.technique ? `<div class="section"><div class="label">Technique:</div><div class="value">${rad.technique}</div></div>` : ''}
          <div class="section">
            <div class="label">Findings:</div>
            <div class="findings">${rad.findings || '-'}</div>
          </div>
          <div class="section">
            <div class="label">Impression:</div>
            <div class="impression">${rad.impression || '-'}</div>
          </div>
          ${rad.recommendations ? `<div class="section"><div class="label">Recommendations:</div><div class="value">${rad.recommendations}</div></div>` : ''}
          <div class="footer">
            <strong>Reported By:</strong> ${rad.radiologistName || '-'}<br>
            <em>This is a computer-generated report.</em>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    },

    getElapsedTime() {
      if (!this.consultationStartTime) return '0 min';
      const mins = Math.floor((new Date() - this.consultationStartTime) / 60000);
      if (mins < 60) return `${mins} min`;
      return `${Math.floor(mins/60)}h ${mins%60}m`;
    },

    loadVisit() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.visit = res.data.data.visit;
              this.consultation.chiefComplaint = this.visit.chiefComplaint || '';
              this.loadTriage();
              this.loadExistingConsultation();
              this.loadLabResults();
              this.startConsultationTimer();

              const returningStatuses = ['Radiology Complete', 'Lab Complete', 'In Radiology', 'In Laboratory', 'Waiting'];
              if (returningStatuses.includes(this.visit.queueStatus)) {
                this.updateVisitStatus('In Consultation');
              }
            } else {
              this.showError('Visit not found');
              this.goTo('queue-management');
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    loadTriage() {
      const data = new FormData();
      data.append("function", "getTriage");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.triage = res.data.data.triage;
              if (this.triage && !this.consultation.chiefComplaint) {
                this.consultation.chiefComplaint = this.triage.chiefComplaint || '';
              }
            }
          })
          .catch((e) => console.error(e));
    },

    loadExistingConsultation() {
      const data = new FormData();
      data.append("function", "getConsultation");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success && res.data.data.consultation) {
              this.existingConsultation = res.data.data.consultation;
              Object.keys(this.consultation).forEach(key => {
                if (this.existingConsultation[key]) {
                  this.consultation[key] = this.existingConsultation[key];
                }
              });
              if (this.existingConsultation.icd10Codes) {
                try {
                  this.selectedICDs = JSON.parse(this.existingConsultation.icd10Codes);
                } catch (e) {
                  console.error(e);
                }
              }
            }
          })
          .catch((e) => console.error(e));
    },

    startConsultationTimer() {
      this.consultationStartTime = new Date();
      const data = new FormData();
      data.append("function", "startConsultation");
      data.append("visitID", this.currentVisitID);
      data.append("doctorID", this.doctorID);
      data.append("doctorName", this.sessionNames);

      execute(data, hospital).catch((e) => console.error(e));
    },

    // ICD-10 Search
    searchICD() {
      if (this.icdSearch.length < 2) {
        this.icdResults = [];
        return;
      }
      const sampleICDs = [
        { code: 'J06.9', description: 'Acute upper respiratory infection, unspecified' },
        { code: 'J18.9', description: 'Pneumonia, unspecified organism' },
        { code: 'A09', description: 'Infectious gastroenteritis and colitis' },
        { code: 'K29.7', description: 'Gastritis, unspecified' },
        { code: 'N39.0', description: 'Urinary tract infection, site not specified' },
        { code: 'J45.9', description: 'Asthma, unspecified' },
        { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
        { code: 'I10', description: 'Essential (primary) hypertension' },
        { code: 'M54.5', description: 'Low back pain' },
        { code: 'R50.9', description: 'Fever, unspecified' },
        { code: 'B34.9', description: 'Viral infection, unspecified' },
        { code: 'R05', description: 'Cough' }
      ];
      const search = this.icdSearch.toLowerCase();
      this.icdResults = sampleICDs.filter(icd =>
          icd.code.toLowerCase().includes(search) ||
          icd.description.toLowerCase().includes(search)
      );
    },

    addICD(icd) {
      if (!this.selectedICDs.find(i => i.code === icd.code)) {
        this.selectedICDs.push(icd);
      }
      this.icdSearch = '';
      this.icdResults = [];
    },

    removeICD(index) {
      this.selectedICDs.splice(index, 1);
    },

    // Lab Tests Methods
    showLabRequestModal() {
      this.labSelectedIncomeType = '';
      this.labSelectedFee = '';
      this.labSelectedFeeData = null;
      this.labFeesAndCharges = [];
      this.selectedLabTests = [];
      this.labPriority = 'ROUTINE';
      this.labClinicalNotes = '';

      this.loadLabIncomeTypes();

      const modal = new bootstrap.Modal(document.getElementById('labRequestModal'));
      modal.show();
    },

    loadLabIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              const allTypes = res.data.data.incomeTypes || [];
              this.labIncomeTypes = allTypes.filter(item => {
                const desc = (item.incomeTypeDescription || '').toLowerCase();
                return desc.includes('lab') ||
                    desc.includes('pathology') ||
                    desc.includes('hematology') ||
                    desc.includes('microbiology') ||
                    desc.includes('chemistry') ||
                    desc.includes('biochemistry') ||
                    desc.includes('serology') ||
                    desc.includes('urinalysis') ||
                    item.incomeTypePrefix === 'LAB' ||
                    item.incomeTypePrefix === 'MEDICAL';
              });

              if (this.labIncomeTypes.length === 0) {
                this.labIncomeTypes = allTypes.filter(item => {
                  const prefix = (item.incomeTypePrefix || '').toUpperCase();
                  return prefix === 'MEDICAL' || prefix === 'HEALTH' || prefix === 'LAB';
                });
              }

              if (this.labIncomeTypes.length === 0) {
                this.labIncomeTypes = allTypes;
              }
            }
          })
          .catch((e) => console.error('Error loading income types:', e));
    },

    onLabIncomeTypeChange() {
      this.labFeesAndCharges = [];
      this.labSelectedFee = '';
      this.labSelectedFeeData = null;

      if (this.labSelectedIncomeType) {
        this.loadLabFeesAndCharges(this.labSelectedIncomeType);
      }
    },

    loadLabFeesAndCharges(incomeTypeId) {
      this.loadingLabFees = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingLabFees = false;
            if (res.data.success) {
              this.labFeesAndCharges = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingLabFees = false;
            console.error('Error loading fees:', e);
          });
    },

    onLabFeeChange() {
      if (this.labSelectedFee) {
        this.labSelectedFeeData = this.labFeesAndCharges.find(f => f.feeId == this.labSelectedFee);
      } else {
        this.labSelectedFeeData = null;
      }
    },

    addLabTestFromBiller() {
      if (!this.labSelectedFeeData) return;

      if (this.selectedLabTests.find(t => t.billerFeeID === this.labSelectedFeeData.feeId)) {
        this.showError('This test is already in the order');
        return;
      }

      const incomeType = this.labIncomeTypes.find(t => t.incomeTypeId == this.labSelectedIncomeType);

      this.selectedLabTests.push({
        testID: null,
        testName: this.labSelectedFeeData.feeDescription,
        unitPrice: parseFloat(this.labSelectedFeeData.unitFeeAmount) || 0,
        billerFeeID: this.labSelectedFeeData.feeId,
        billerIncomeTypeID: this.labSelectedIncomeType,
        incomeTypeDesc: incomeType ? incomeType.incomeTypeDescription : '',
        accountDesc: this.labSelectedFeeData.accountDesc || ''
      });

      this.labSelectedFee = '';
      this.labSelectedFeeData = null;
    },

    removeLabTest(index) {
      this.selectedLabTests.splice(index, 1);
    },

    submitLabRequest() {
      if (this.selectedLabTests.length === 0) return;

      this.submittingLab = true;
      const testNames = this.selectedLabTests.map(t => t.testName).join(', ');

      const data = new FormData();
      data.append("function", "addLabRequest");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("requestedBy", this.doctorID);
      data.append("requestedByName", this.sessionNames);
      data.append("priority", this.labPriority);
      data.append("clinicalNotes", this.labClinicalNotes);
      data.append("tests", JSON.stringify(this.selectedLabTests.map(t => ({
        testID: t.testID || null,
        testName: t.testName,
        unitPrice: t.unitPrice || 0,
        billerFeeID: t.billerFeeID || '',
        billerIncomeTypeID: t.billerIncomeTypeID || ''
      }))));
      data.append("totalAmount", this.labOrderTotal);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.submittingLab = false;
            if (res.data.success) {
              this.showSuccess('Lab request submitted successfully');
              bootstrap.Modal.getInstance(document.getElementById('labRequestModal')).hide();

              if (testNames) {
                this.consultation.investigationsOrdered += (this.consultation.investigationsOrdered ? '\n' : '') +
                    'Lab: ' + testNames;
              }

              this.selectedLabTests = [];
              this.labClinicalNotes = '';
              this.labSelectedIncomeType = '';
              this.labSelectedFee = '';
              this.labSelectedFeeData = null;

              this.loadLabResults();
            } else {
              this.showError(res.data.message || 'Failed to submit lab request');
            }
          })
          .catch((e) => {
            this.submittingLab = false;
            console.error(e);
            this.showError('Error submitting lab request');
          });
    },

    viewPatientHistory() {
      this.$router.push('/patient-profile/' + this.visit.patientID);
    },

    printSummary() {
      window.print();
    },

    saveDraft() {
      this.saveConsultation(true);
    },

    saveConsultation(isDraft = false) {
      if (!isDraft && !this.canSubmit) return;

      this.saving = true;

      const data = new FormData();
      data.append("function", "addConsultation");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("doctorID", this.doctorID);
      data.append("doctorName", this.sessionNames);

      Object.keys(this.consultation).forEach(key => {
        data.append(key, this.consultation[key] || '');
      });

      data.append("icd10Codes", JSON.stringify(this.selectedICDs));
      data.append("status", isDraft ? 'Draft' : 'Completed');
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.saving = false;
            if (res.data.success) {
              this.showSuccess(isDraft ? 'Draft saved' : 'Consultation completed successfully');
              if (!isDraft) {
                setTimeout(() => {
                  this.$router.push('/queue-management');
                }, 1500);
              }
            } else {
              this.showError(res.data.message || 'Failed to save consultation');
            }
          })
          .catch((e) => {
            this.saving = false;
            console.error(e);
            this.showError('Error saving consultation');
          });
    }
  },
  mounted() {
    this.currentVisitID = this.visitID || this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.doctorID = sessionStorage.getItem("staffID") || sessionStorage.getItem("userID") || '';

    if (this.currentVisitID) {
      this.loadVisit();
    } else {
      this.showError('No visit specified');
      this.goTo('queue-management');
    }

    setInterval(() => {
      this.$forceUpdate();
    }, 60000);
  }
};
</script>
<style scoped>
/* Avatar */
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%; font-weight: 600; color: #fff;
}

/* Colors */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-pink-subtle { background-color: rgba(232, 62, 140, 0.15); }
.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.15); }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.15); }
.bg-pink { background-color: #e83e8c !important; }
.text-pink { color: #e83e8c !important; }

/* Queue Badge */
.queue-badge-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #556ee6 0%, #3b5de7 100%);
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
}

/* Vitals Mini */
.vital-mini {
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
  text-align: center;
}

.vital-label {
  display: block;
  font-size: 0.7rem;
  color: #74788d;
  text-transform: uppercase;
}

.vital-value {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
}

/* Nav Tabs */
.nav-tabs-custom .nav-link {
  color: #495057;
  border: none;
  padding: 0.75rem 1rem;
  font-weight: 500;
}

.nav-tabs-custom .nav-link.active {
  color: #556ee6;
  border-bottom: 2px solid #556ee6;
  background: transparent;
}

.nav-tabs-custom .nav-link:hover:not(.active) {
  color: #556ee6;
}

/* ICD Results */
.icd-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.icd-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f3f3;
  transition: background 0.2s;
}

.icd-item:hover {
  background: #f8f9fa;
}

.icd-item:last-child {
  border-bottom: none;
}

.icd-code {
  font-weight: 600;
  color: #556ee6;
  margin-right: 8px;
}

.icd-desc {
  color: #495057;
}

/* Card */
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* Lab Results Empty State */
.empty-state-icon {
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-icon i {
  font-size: 28px;
  color: #adb5bd;
}

/* Soft Buttons */
.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.15);
  color: #556ee6;
}

.btn-soft-primary:hover {
  background-color: #556ee6;
  color: #fff;
}

.btn-soft-success {
  background-color: rgba(52, 195, 143, 0.15);
  color: #34c38f;
}

.btn-soft-success:hover {
  background-color: #34c38f;
  color: #fff;
}

.btn-soft-warning {
  background-color: rgba(241, 180, 76, 0.15);
  color: #f1b44c;
}

.btn-soft-warning:hover {
  background-color: #f1b44c;
  color: #fff;
}

/* Fix dropdown/select text visibility in all modals */
.modal .form-select,
.modal .form-control,
.modal select,
.modal input,
.modal textarea {
  color: #495057 !important;
  background-color: #fff !important;
}

.modal .form-select option {
  color: #495057 !important;
  background-color: #fff !important;
}

.modal-content {
  background-color: #fff !important;
}

.modal-body {
  color: #212529;
  background-color: #fff !important;
}

.modal-body .form-label {
  color: #495057;
}

/* All modals - fixed dropdown visibility */
#radiologyModal .modal-content,
#radiologyModal .modal-body,
#labRequestModal .modal-content,
#labRequestModal .modal-body,
#prescriptionModal .modal-content,
#prescriptionModal .modal-body,
#surgeryModal .modal-content,
#surgeryModal .modal-body {
  background-color: #fff !important;
}

#radiologyModal .form-select,
#radiologyModal .form-control,
#labRequestModal .form-select,
#labRequestModal .form-control,
#prescriptionModal .form-select,
#prescriptionModal .form-control,
#surgeryModal .form-select,
#surgeryModal .form-control {
  color: #212529 !important;
  background-color: #fff !important;
  border: 1px solid #ced4da;
}

#radiologyModal select option,
#labRequestModal select option,
#prescriptionModal select option,
#surgeryModal select option {
  color: #212529 !important;
  background-color: #fff !important;
  padding: 8px;
}
</style>